import { useState } from 'react';
import { RefreshCw, LogOut } from 'lucide-react';
import { useSession } from '@repo/api-client/react';
import { Button } from '@/components/ui/button';
import { InvoyssLogo } from '@/features/brand/InvoyssLogo';

/**
 * Full-screen gate when the session resolver cannot reach the API.
 * Mirrors mobile `apps/mobile/app/maintenance.tsx`.
 */
export function MaintenanceScreen() {
  const session = useSession();
  const [refreshing, setRefreshing] = useState(false);

  async function handleRetry() {
    if (refreshing) return;
    setRefreshing(true);
    try {
      await session.refresh();
    } finally {
      setRefreshing(false);
    }
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background px-6">
      <div className="mb-8">
        <InvoyssLogo size="md" />
      </div>
      <div className="max-w-md text-center">
        <h1 className="font-display text-2xl font-semibold tracking-tight text-foreground">
          We can&apos;t reach our servers
        </h1>
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
          Your data is safe — we just can&apos;t verify your session right now.
          Check your connection and try again, or sign out to switch accounts.
        </p>
        <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center">
          <Button onClick={handleRetry} loading={refreshing}>
            <RefreshCw className="size-4" /> Try again
          </Button>
          <Button variant="outline" onClick={() => session.signOut()}>
            <LogOut className="size-4" /> Sign out
          </Button>
        </div>
        {session.status === 'error' ? (
          <p className="mt-6 text-xs text-muted-foreground">
            Still unreachable. If this persists, check{' '}
            <a
              href="https://status.invoyss.com"
              target="_blank"
              rel="noreferrer"
              className="underline hover:text-foreground"
            >
              status.invoyss.com
            </a>
            .
          </p>
        ) : null}
      </div>
    </div>
  );
}
